// components/WhatsAppButton.tsx
"use client"
import { Icon } from "@iconify/react/dist/iconify.js"
import Link from "next/link"
import { useLocale, useTranslations } from "next-intl"
import { courseTitle } from "@/lib/courseTitleTranslations"

const WHATSAPP_NUMBER = "923266866632" // static number

interface WhatsAppButtonProps {
  label?: string
  courseSlug?: string       // optional: auto inserts course name
  message?: string // optional: full custom message
  className?: string
  // 'solid' (default) — WhatsApp is the one and only action being offered
  // (e.g. the plain /contact page). 'light' — a soft tint instead, for
  // whenever this sits next to a solid primary CTA (Enroll Now, Send a
  // Message) so the two don't compete as two equally-bold buttons; the
  // full-color WhatsApp glyph still makes it instantly recognizable either
  // way.
  variant?: 'solid' | 'light'
}

const VARIANT_CLS: Record<'solid' | 'light', string> = {
  solid: 'bg-green-600 text-white hover:bg-green-700 shadow-md',
  light: 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100',
}

const WhatsAppButton = ({
  label,
  courseSlug,
  message,
  className,
  variant = 'solid',
}: WhatsAppButtonProps) => {
  const locale = useLocale()
  const t = useTranslations("Common")
  // Build default or course-based message
  const fallbackCourseTitle = courseSlug?.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) ?? ""
  const translatedCourseTitle = courseSlug ? courseTitle(courseSlug, fallbackCourseTitle, locale) : ""
  const courseMessage = courseSlug
    ? t("enrollCourseMessage", { course: translatedCourseTitle })
    : t("whatsappGreeting")

  // If custom message is passed, use that, otherwise fallback
  const finalMessage = message || courseMessage

  // Encode message for WhatsApp link
  const encodedMessage = encodeURIComponent(finalMessage)
  const link = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`

  return (
    <Link
      href={link}
      target="_blank"
      // Same padding/gap/type-scale as PrimaryButton/SecondaryButton — this
      // used to run its own larger scale (text-lg label, text-2xl icon,
      // gap-3, shadow-lg), so it visually didn't match its neighbors
      // whenever it sat in the same button row as those.
      className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition duration-300 w-fit ${VARIANT_CLS[variant]} ${className ?? ''}`}
    >
      <Icon icon="logos:whatsapp-icon" className="text-lg" />
      {label ?? t('chatOnWhatsapp')}
    </Link>
  )
}

export default WhatsAppButton
